/**
 * Filtros para listado de citas CRM (estilo oportunidades/contactos).
 * Delegación, modalidad, agente, rango de fechas y orden (más recientes / más antiguas).
 * @module components/appointments/AppointmentFilters
 */

import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DelegationSelector } from './DelegationSelector';
import { getModalityLabel } from '@/utils/appointmentHelpers';
import type { AppointmentListParams, AppointmentModality, AppointmentSortField, AppointmentSortOrder } from '@/types/appointment.types';
import { FunnelIcon, XMarkIcon } from '@heroicons/react/24/outline';

export interface AppointmentFiltersState
  extends Pick<
    AppointmentListParams,
    'delegation_id' | 'modality' | 'agent_id' | 'start_date' | 'end_date' | 'sort_by' | 'sort_order'
  > {}

interface AppointmentFiltersProps {
  filters: AppointmentFiltersState;
  onFiltersChange: (filters: AppointmentFiltersState) => void;
  availableAgents?: Array<{ id: string; name: string }>;
  loadingAgents?: boolean;
}

const DEFAULT_SORT: { sort_by: AppointmentSortField; sort_order: AppointmentSortOrder } = {
  sort_by: 'scheduled_at',
  sort_order: 'desc',
};

export function AppointmentFilters({
  filters,
  onFiltersChange,
  availableAgents = [],
  loadingAgents = false,
}: AppointmentFiltersProps) {
  const [isOpen, setIsOpen] = useState(false);

  const updateFilter = <K extends keyof AppointmentFiltersState>(key: K, value: AppointmentFiltersState[K]) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const activeCount =
    (filters.delegation_id ? 1 : 0) +
    (filters.modality ? 1 : 0) +
    (filters.agent_id ? 1 : 0) +
    (filters.start_date ? 1 : 0) +
    (filters.end_date ? 1 : 0) +
    (filters.sort_by && (filters.sort_by !== DEFAULT_SORT.sort_by || filters.sort_order !== DEFAULT_SORT.sort_order) ? 1 : 0);

  const hasActiveFilters = activeCount > 0;

  const clearAllFilters = () => {
    onFiltersChange({
      delegation_id: undefined,
      modality: undefined,
      agent_id: undefined,
      start_date: undefined,
      end_date: undefined,
      sort_by: DEFAULT_SORT.sort_by,
      sort_order: DEFAULT_SORT.sort_order,
    });
    setIsOpen(false);
  };

  return (
    <Card>
      <CardContent className="pt-4 sm:pt-6">
        <div className="flex flex-col gap-4">
          {/* Fila principal: delegación, modalidad, orden y botón Filtros */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 flex-wrap">
            <DelegationSelector
              value={filters.delegation_id}
              onChange={(v) => updateFilter('delegation_id', v)}
              label=""
              placeholder="Todas las delegaciones"
              showAllOption
              className="w-full sm:w-48 min-w-0"
            />
            <div className="w-full sm:w-40 min-w-0">
              <Label htmlFor="appointment-modality-filter" className="sr-only">
                Modalidad
              </Label>
              <select
                id="appointment-modality-filter"
                value={filters.modality ?? ''}
                onChange={(e) => updateFilter('modality', (e.target.value || undefined) as AppointmentModality | undefined)}
                className="w-full min-h-[44px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                aria-label="Filtrar por modalidad"
              >
                <option value="">Todas las modalidades</option>
                <option value="in_person">{getModalityLabel('in_person')}</option>
                <option value="video_call">{getModalityLabel('video_call')}</option>
              </select>
            </div>
            <div className="w-full sm:w-44 min-w-0">
              <Label htmlFor="appointment-sort" className="sr-only">
                Ordenar por fecha
              </Label>
              <select
                id="appointment-sort"
                value={filters.sort_order === 'asc' ? 'asc' : 'desc'}
                onChange={(e) => {
                  const order = (e.target.value || 'desc') as AppointmentSortOrder;
                  updateFilter('sort_order', order);
                  updateFilter('sort_by', 'scheduled_at');
                }}
                className="w-full min-h-[44px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                aria-label="Ordenar citas"
              >
                <option value="desc">Más recientes primero</option>
                <option value="asc">Más antiguas primero</option>
              </select>
            </div>
            <div className="flex items-center gap-2 sm:ml-auto">
              <Button
                variant="outline"
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-2 min-h-[44px]"
              >
                <FunnelIcon className="w-4 h-4" />
                <span>Filtros</span>
                {hasActiveFilters && (
                  <span className="ml-1 bg-primary text-primary-foreground rounded-full min-w-[22px] h-5 flex items-center justify-center text-xs px-1.5">
                    {activeCount}
                  </span>
                )}
              </Button>
              {hasActiveFilters && (
                <Button
                  variant="outline"
                  onClick={clearAllFilters}
                  className="text-red-600 hover:text-red-700 min-h-[44px]"
                >
                  <XMarkIcon className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Limpiar</span>
                </Button>
              )}
            </div>
          </div>

          {/* Panel colapsable: agente y rango de fechas */}
          {isOpen && (
            <div className="pt-4 border-t space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <Label htmlFor="appointment-agent-filter" className="text-sm font-medium text-gray-700 mb-2 block">
                    Responsable
                  </Label>
                  <select
                    id="appointment-agent-filter"
                    value={filters.agent_id ?? ''}
                    onChange={(e) => updateFilter('agent_id', e.target.value || undefined)}
                    disabled={loadingAgents}
                    className="w-full min-h-[44px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm disabled:bg-gray-100"
                    aria-label="Filtrar por responsable"
                  >
                    <option value="">
                      {loadingAgents ? 'Cargando...' : 'Todos los responsables'}
                    </option>
                    {availableAgents.map((agent) => (
                      <option key={agent.id} value={agent.id}>
                        {agent.name}
                      </option>
                    ))}
                    {!loadingAgents && availableAgents.length === 0 && (
                      <option value="" disabled>
                        Sin responsables
                      </option>
                    )}
                  </select>
                </div>
                <div>
                  <Label htmlFor="appointment-start-date" className="text-sm font-medium text-gray-700 mb-2 block">
                    Fecha desde
                  </Label>
                  <Input
                    id="appointment-start-date"
                    type="date"
                    value={filters.start_date ?? ''}
                    onChange={(e) => updateFilter('start_date', e.target.value || undefined)}
                    className="min-h-[44px]"
                    aria-label="Citas desde fecha"
                  />
                </div>
                <div>
                  <Label htmlFor="appointment-end-date" className="text-sm font-medium text-gray-700 mb-2 block">
                    Fecha hasta
                  </Label>
                  <Input
                    id="appointment-end-date"
                    type="date"
                    value={filters.end_date ?? ''}
                    onChange={(e) => updateFilter('end_date', e.target.value || undefined)}
                    className="min-h-[44px]"
                    aria-label="Citas hasta fecha"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
